<template>
  <div class="models-page">
    <header class="header">
      <div class="header-left">
        <span class="logo">🫀</span>
        <h1>3D 人体解剖模型查看器</h1>
      </div>
      <p class="subtitle">支持三维浏览、剖面分析与病变对比</p>
    </header>

    <div class="main-layout">
      <!-- 左侧模型库 -->
      <aside class="sidebar">
        <h2 class="panel-title">模型库</h2>
        <ul class="model-list">
          <li
            v-for="item in modelLibrary"
            :key="item.id"
            :class="{ active: currentModelId === item.id }"
            @click="selectModel(item.id)"
          >
            <span class="model-icon" :class="{ 'is-svg': item.iconSvg }">
              <span v-if="item.iconSvg" class="model-icon-svg" v-html="item.iconSvg" />
              <template v-else>{{ item.icon }}</template>
            </span>
            <div class="model-info">
              <span class="model-name">{{ item.name }}</span>
              <span class="model-id">{{ item.id }}</span>
            </div>
          </li>
        </ul>

        <h2 class="panel-title mt">组件结构</h2>
        <ul v-if="currentModel" class="component-list">
          <li v-for="comp in currentModel.components" :key="comp.id">
            <label>
              <input
                type="checkbox"
                :checked="visibleComponents[comp.id]"
                @change="toggleComponent(comp.id, $event.target.checked)"
              />
              <span class="comp-dot" :style="{ background: getComponentColor(comp.id) }" />
              {{ comp.name }}
            </label>
          </li>
        </ul>

        <h2 class="panel-title mt">病变标记</h2>
        <ul v-if="currentModel && currentModel.lesions.length" class="lesion-list">
          <li
            v-for="lesion in currentModel.lesions"
            :key="lesion.id"
            :class="{ active: selectedLesionId === lesion.id }"
            @click="selectLesion(lesion.id)"
          >
            <span class="lesion-dot" />
            {{ lesion.name }}
          </li>
        </ul>
        <p v-else class="empty-tip">当前模型无病变标记</p>
      </aside>

      <!-- 中央 3D 视图 -->
      <main class="viewer-area">
        <AnatomyViewer
          ref="viewer"
          :model-data="currentModel"
          :clip-enabled="clipEnabled"
          :clip-offset.sync="clipOffset"
          :clip-axis="clipAxis"
          :visible-components="visibleComponents"
          :compare-mode.sync="compareMode"
          :selected-lesion-id="selectedLesionId"
          @lesion-select="selectLesion"
          @hotspot-click="onHotspotClick"
        />
        <div v-if="modelLoading" class="loading overlay">
          <span class="loading-spinner" />
          正在加载真实解剖模型...
        </div>
        <div v-else-if="loadError" class="loading error overlay">
          {{ loadError }}
          <button class="retry-btn" @click="selectModel(currentModelId)">重试</button>
        </div>
      </main>

      <!-- 右侧工具栏 -->
      <aside class="toolbar">
        <h2 class="panel-title">视图工具</h2>
        <div class="tool-group">
          <button class="tool-btn" @click="resetView">
            <span>↺</span> 重置视角
          </button>
          <button
            class="tool-btn"
            :class="{ active: clipEnabled }"
            @click="clipEnabled = !clipEnabled"
          >
            <span>✂</span> 剖面视图
          </button>
        </div>

        <h2 class="panel-title mt">剖面设置</h2>
        <div class="tool-group">
          <label class="slider-label">
            剖切轴
            <select v-model="clipAxis" @change="updateClipAxis">
              <option value="x">X 轴（左右）</option>
              <option value="y">Y 轴（上下）</option>
              <option value="z">Z 轴（前后）</option>
            </select>
          </label>
          <label v-if="clipEnabled" class="slider-label">
            剖面位置
            <input v-model.number="clipOffset" type="range" min="-1.5" max="1.5" step="0.01" />
          </label>
        </div>

        <h2 class="panel-title mt">模型信息</h2>
        <div v-if="currentModel" class="info-card">
          <h3>{{ currentModel.name }}</h3>
          <p>{{ currentModel.description }}</p>
          <div class="stats">
            <div class="stat">
              <span class="stat-val">{{ currentModel.components.length }}</span>
              <span class="stat-label">组件</span>
            </div>
            <div class="stat">
              <span class="stat-val">{{ currentModel.hotspots.length }}</span>
              <span class="stat-label">标注点</span>
            </div>
            <div class="stat">
              <span class="stat-val">{{ currentModel.lesions.length }}</span>
              <span class="stat-label">病变点</span>
            </div>
          </div>
        </div>

        <div v-if="lastHotspot" class="hotspot-info">
          <h2 class="panel-title mt">当前标注</h2>
          <p>{{ lastHotspot.label }}</p>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import AnatomyViewer from '@/components/AnatomyViewer.vue'
import { MODEL_LIBRARY, loadModel, buildVisibleMap } from '@/models/anatomyModels'

const COMPONENT_COLORS = {
  lv: '#922b21',
  rv: '#c0392b',
  la: '#f5b7b1',
  ra: '#f5b7b1',
  aorta: '#e74c3c',
  pa: '#e74c3c',
  svc: '#3498db',
  mitral: '#d63031',
  tricuspid: '#e17055',
  caudate: '#6b3410',
  cerebrum: '#d7bde2',
  cerebellum: '#c39bd3',
  brainstem: '#c39bd3',
  left_lung: '#e8a598',
  right_lung: '#e8a598',
  trachea: '#d4a574',
  right_lobe: '#a0522d',
  left_lobe: '#8b6914',
  capsule: '#6b3e26',
  ligament: '#b8956a',
  impression: '#5c4033',
  gallbladder: '#2ecc71',
  portal_vein: '#3498db',
  left_kidney: '#9b59b6',
  right_kidney: '#9b59b6',
  left_pelvis: '#e67e22',
  right_pelvis: '#e67e22',
  ureter: '#3498db',
}

export default {
  name: 'App',
  components: { AnatomyViewer },
  data() {
    return {
      modelLibrary: MODEL_LIBRARY,
      currentModelId: 'heart',
      currentModel: null,
      visibleComponents: {},
      modelLoading: false,
      loadError: null,
      clipEnabled: false,
      clipOffset: 0,
      clipAxis: 'z',
      compareMode: 'lesion',
      selectedLesionId: null,
      lastHotspot: null,
    }
  },
  created() {
    this.selectModel('heart')
  },
  activated() {
    if (!this.currentModel && !this.modelLoading) {
      this.selectModel(this.currentModelId)
    }
    this.$nextTick(() => {
      if (this.$refs.viewer) {
        if (this.$refs.viewer.onResize) this.$refs.viewer.onResize()
        if (this.$refs.viewer.ensureCanvasSize) this.$refs.viewer.ensureCanvasSize()
      }
    })
  },
  methods: {
    async selectModel(id) {
      if (this.modelLoading) return
      this.currentModelId = id
      this.selectedLesionId = null
      this.compareMode = 'lesion'
      this.lastHotspot = null
      this.modelLoading = true
      this.loadError = null
      try {
        const model = await loadModel(id)
        if (!model) throw new Error('未找到该模型')
        this.currentModel = model
        this.visibleComponents = buildVisibleMap(model)
      } catch (err) {
        console.error('[3DModels] load failed:', err)
        this.loadError = '模型加载失败，请检查 public/models/anatomy/ 下 GLB 文件是否完整'
        this.currentModel = null
      } finally {
        this.modelLoading = false
        this.$nextTick(() => {
          if (this.$refs.viewer) {
            if (this.$refs.viewer.onResize) this.$refs.viewer.onResize()
            if (this.$refs.viewer.ensureCanvasSize) this.$refs.viewer.ensureCanvasSize()
          }
        })
      }
    },

    toggleComponent(id, visible) {
      this.$set(this.visibleComponents, id, visible)
    },

    selectLesion(id) {
      this.selectedLesionId = id
      if (id) this.compareMode = 'split'
    },

    resetView() {
      this.selectedLesionId = null
      this.clipEnabled = false
      this.clipOffset = 0
      if (this.$refs.viewer) {
        this.$refs.viewer.resetCamera()
      }
    },

    updateClipAxis() {
      this.clipOffset = 0
    },

    getComponentColor(id) {
      return COMPONENT_COLORS[id] || '#64748b'
    },

    onHotspotClick(data) {
      this.lastHotspot = data
    },
  },
}
</script>

<style lang="scss" scoped>
*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html,
body,
#app {
  height: 100%;
  font-family: 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  background: #0f172a;
  color: #e2e8f0;
}

.app {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.models-page {
  display: flex;
  flex-direction: column;
  /* 填满 Main.vue 的 el-main 内容区（抵消 padding 后全屏展示） */
  margin: -20px -36px;
  width: calc(100% + 72px);
  height: calc(100% + 40px);
  min-height: 560px;
  padding: 0 20px 10px;
  background: #fff;
  box-sizing: border-box;
}

.header {
  padding: 12px 24px;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-bottom: 1px solid rgba(148, 163, 184, 0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo {
  font-size: 28px;
}

.header h1 {
  font-size: 18px;
  font-weight: 600;
  color: #f1f5f9;
}

.subtitle {
  font-size: 12px;
  color: #64748b;
}

.main-layout {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.sidebar,
.toolbar {
  width: 240px;
  flex-shrink: 0;
  background: #1e293b;
  border-right: 1px solid rgba(148, 163, 184, 0.1);
  overflow-y: auto;
  padding: 16px;
}

.toolbar {
  border-right: none;
  border-left: 1px solid rgba(148, 163, 184, 0.1);
}

.panel-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  margin-bottom: 10px;
}

.panel-title.mt {
  margin-top: 20px;
}

.model-list {
  list-style: none;
}

.model-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  margin-bottom: 4px;
}

.model-list li:hover {
  background: rgba(148, 163, 184, 0.1);
}

.model-list li.active {
  background: rgba(56, 189, 248, 0.12);
  border: 1px solid rgba(56, 189, 248, 0.3);
}

.model-icon {
  font-size: 22px;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.model-icon.is-svg {
  font-size: 0;
}

.model-icon-svg {
  display: inline-flex;
  width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
}

.model-icon-svg >>> svg {
  width: 100%;
  height: 100%;
}

.model-info {
  display: flex;
  flex-direction: column;
}

.model-name {
  font-size: 14px;
  color: #f1f5f9;
}

.model-id {
  font-size: 11px;
  color: #64748b;
}

.component-list,
.lesion-list {
  list-style: none;
}

.component-list li,
.lesion-list li {
  padding: 6px 0;
}

.component-list label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #cbd5e1;
  cursor: pointer;
}

.comp-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.lesion-list li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 13px;
  color: #cbd5e1;
  cursor: pointer;
  transition: background 0.2s;
}

.lesion-list li:hover {
  background: rgba(142, 68, 173, 0.1);
}

.lesion-list li.active {
  background: rgba(142, 68, 173, 0.2);
  color: #c084fc;
}

.lesion-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #8e44ad;
  flex-shrink: 0;
}

.empty-tip {
  font-size: 12px;
  color: #64748b;
}

.viewer-area {
  flex: 1;
  min-height: 0;
  position: relative;
  background: radial-gradient(ellipse at center, #1e293b 0%, #0f172a 70%);
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 100%;
  color: #94a3b8;
  font-size: 14px;
}

.loading.overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  background: rgba(15, 23, 42, 0.72);
  backdrop-filter: blur(4px);
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(148, 163, 184, 0.2);
  border-top-color: #38bdf8;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading.error {
  color: #f87171;
  padding: 0 24px;
  text-align: center;
  line-height: 1.6;
}

.retry-btn {
  margin-top: 8px;
  padding: 8px 16px;
  background: rgba(56, 189, 248, 0.15);
  border: 1px solid rgba(56, 189, 248, 0.4);
  border-radius: 6px;
  color: #38bdf8;
  cursor: pointer;
  font-size: 13px;
}

.retry-btn:hover {
  background: rgba(56, 189, 248, 0.25);
}

.tool-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tool-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(148, 163, 184, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.15);
  border-radius: 8px;
  color: #cbd5e1;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.tool-btn:hover {
  background: rgba(148, 163, 184, 0.15);
  color: #f1f5f9;
}

.tool-btn.active {
  background: rgba(56, 189, 248, 0.12);
  border-color: rgba(56, 189, 248, 0.4);
  color: #38bdf8;
}

.slider-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: #94a3b8;
}

.slider-label select,
.slider-label input[type='range'] {
  width: 100%;
}

.slider-label select {
  padding: 6px 8px;
  background: #0f172a;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 6px;
  color: #e2e8f0;
  font-size: 12px;
}

.info-card {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.12);
  border-radius: 10px;
  padding: 14px;
}

.info-card h3 {
  font-size: 15px;
  color: #f1f5f9;
  margin-bottom: 6px;
}

.info-card p {
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.5;
  margin-bottom: 12px;
}

.stats {
  display: flex;
  gap: 12px;
}

.stat {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-val {
  font-size: 18px;
  font-weight: 600;
  color: #38bdf8;
}

.stat-label {
  font-size: 10px;
  color: #64748b;
}

.hotspot-info p {
  font-size: 13px;
  color: #fbbf24;
  padding: 8px 12px;
  background: rgba(251, 191, 36, 0.08);
  border-radius: 6px;
}
</style>
